import { useCurrentClient } from '@pbk-web/state';
import type { SeverityLevel } from '@sentry/react';
import * as Sentry from '@sentry/react';
import type { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';

import { useCurrentUser } from '../global-state/Auth';

export interface ApiErrorInfo {
  error?: Error;
  response?: AxiosResponse;
}

export interface ErrorDetails {
  dialog?: boolean;
  component?: string;
  breadcrumbs?: Sentry.Breadcrumb[];
  apiError?: ApiErrorInfo;
  level?: SeverityLevel;
  fn?: string;
  tags?: Record<string, string>;
}

const DEFAULT = {
  dialog: false,
  breadcrumbs: [] as Sentry.Breadcrumb[],
  level: 'info' as SeverityLevel,
};

interface ReturnType {
  captureError: (error: Error, details: ErrorDetails) => void;
  captureApiError: (apiError: ApiErrorInfo) => void;
}

const useSentry = (): ReturnType => {
  const user = useCurrentUser((x) => x.value);
  const client = useCurrentClient((x) => x.value);

  /**
   * Captures Sentry exception with added details
   * @param error exception sent to Sentry
   * @param details extra details to send with exception
   * @example
   * details
   * {
   *  dialog: boolean = false
   *  component: string
   *  breadcrumbs: Sentry.Breadcrumb[] = []
   *  httpCode: number | null
   *  level: Sentry.Severity = Sentry.Severity.Info
   *  fn: string
   * }
   */
  const captureError = useCallback(
    (error: Error, details: ErrorDetails = DEFAULT) => {
      const { width, height } = window.screen;
      const {
        dialog,
        component,
        breadcrumbs,
        apiError,
        level,
        fn,
        tags: reqTags,
      } = {
        ...DEFAULT,
        ...details,
      };

      const tags = {
        resolution: `${width} x ${height}`,
        component,
        fn,
        ...reqTags,
      };

      Sentry.captureException(error, (scope) => {
        Object.entries(tags).forEach(([key, value]) => {
          if (value) scope.setTag(key, value);
        });

        if (apiError) scope.setExtra('API Error', JSON.stringify(apiError));

        scope.setLevel(level);

        breadcrumbs.forEach((bc) => scope.addBreadcrumb(bc));

        // will trigger Sentry error dialog
        if (dialog) scope.setExtra('Dialog', true);

        // set user details
        scope.setUser({
          id: user?.id,
          'Client ID': client?.id,
        });

        return scope;
      });
    },
    [client?.id, user?.id],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const describeResponse = (response?: AxiosResponse<any, any> | null) => {
    if (!response) return null;

    const { status, statusText, config } = response;
    const method = config.method?.toUpperCase();
    const url = config.url;

    return `API Error: ${method} ${url}: [${status}] ${statusText}`;
  };

  const captureApiError = useCallback(
    (apiError: ApiErrorInfo) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const description = apiError.error ? (apiError.error.message ?? describeResponse(apiError.error as any)) : describeResponse(apiError.response);
      captureError(new Error(description ?? 'Unknown API Error'), {
        apiError,
        level: 'error',
      });
    },
    [captureError],
  );

  return useMemo(
    () => ({
      captureError,
      captureApiError,
    }),
    [captureApiError, captureError],
  );
};

export default useSentry;
